import React, { useEffect, useState } from "react";
import api from "../api";
import { ShieldCheck, TrendingUp, Lock, Coins, Sparkles, Building2 } from "lucide-react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

/**
 * STEP 14 · Fiat Stability Lock info banner.
 * Renders the mandatory dashboard notice + live TGC market ticker.
 */
export function FiatStabilityBanner({ compact = false }) {
  const [market, setMarket] = useState(null);
  const load = async () => {
    try { const r = await api.get("/banking/tgc-market"); setMarket(r.data); } catch (_e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 15000);

  return (
    <div data-testid="fiat-stability-banner"
      style={{
        marginBottom: 22, borderRadius: 14, overflow: "hidden",
        border: "1px solid rgba(255,215,0,0.35)",
        background: "linear-gradient(120deg, rgba(255,215,0,0.10) 0%, rgba(0,242,255,0.06) 50%, rgba(16,244,167,0.08) 100%)",
      }}>
      <div style={{ padding: "16px 20px", display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,215,0,0.15)", border: "1px solid #FFD70055", flexShrink: 0 }}>
          <ShieldCheck size={26} color="#FFD700" />
        </div>
        <div style={{ flex: 1, minWidth: 300 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#FFD700", fontWeight: 800 }}>
            Pre-Launch Corporate Banking Tier · Active
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: "#E7ECFF", lineHeight: 1.55 }}>
            <b style={{ color: "#00F2FF" }}>100 % of capital entry</b> is backed by a Hybrid TGC Token Deposit.
            Your balance compounds like a snowball through network deflation.
            <br />
            <b style={{ color: "#10F4A7" }}>Volatility Protection Protocol (Fiat Stability Lock) Enforced:</b>{" "}
            system assets are locked at peak performance value and can never decrease, providing
            total security for enterprise payroll funds and international B2B settlements.
          </div>
        </div>
        {!compact && market && (
          <div style={{
            padding: "12px 16px", borderRadius: 10,
            background: "rgba(0,0,0,0.35)", border: "1px solid rgba(0,242,255,0.35)",
            minWidth: 220, alignSelf: "center",
          }} data-testid="tgc-market-mini-ticker">
            <div style={{ fontSize: 10, letterSpacing: 1, color: "#00F2FF", textTransform: "uppercase" }}>TGC / USDT</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#00F2FF", fontFeatureSettings: '"tnum"' }}>
              ${Number(market.tgc_price_usdt || 0).toFixed(6)}
            </div>
            <div style={{ fontSize: 11, color: "#10F4A7", display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
              <TrendingUp size={11} /> +{Number(market.price_appreciation_pct || 0).toFixed(4)}% since genesis
            </div>
            <div style={{ fontSize: 10, color: "#5F6884", marginTop: 4 }}>
              burned {Number(market.burned_tgc || 0).toFixed(2)} TGC · {market.cumulative_burn_events} events
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * STEP 14 · Corporate Banking + Fiat Stability Lock ledger panel.
 * Full page inside the Merchant 2.0 nav.
 */
export function CorporateBankingPage() {
  const [state, setState] = useState({ exists: false });
  const [market, setMarket] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ legal_name: "", jurisdiction: "AIFC", primary_currency: "USDT", additional_currencies: ["USD", "EUR"] });
  const [fundForm, setFundForm] = useState({ currency: "USDT", amount: 1000, convert_to_tgc: true });

  const reload = async () => {
    try {
      const [c, m] = await Promise.all([
        api.get("/banking/corp-account"),
        api.get("/banking/tgc-market"),
      ]);
      setState(c.data);
      setMarket(m.data);
    } catch (_e) { /* noop */ }
  };
  useEffect(() => { reload(); }, []);
  useAutoRefresh(reload, 15000);

  const openAccount = async () => {
    if (!form.legal_name) { alert("Enter your corporate legal name"); return; }
    setBusy(true);
    try { await api.post("/banking/corp-account/open", form); await reload(); }
    catch (e) { alert("Open failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const fund = async () => {
    setBusy(true);
    try { await api.post("/banking/corp-account/fund", fundForm); await reload(); }
    catch (e) { alert("Fund failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const ledger = state.ledger;
  const corp = state.corp;
  const fmt = (n, d = 4) => Number(n || 0).toFixed(d);

  return (
    <div data-testid="corporate-banking-page">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
        <Building2 size={26} color="#FFD700" />
        <h2 className="page-title" style={{ margin: 0 }}>Hybrid Corporate Banking</h2>
      </div>
      <p className="page-sub" style={{ marginTop: 6 }}>
        Multi-currency corporate account · TGC Snowball Ledger · Fiat Stability Lock
      </p>

      <FiatStabilityBanner />

      {/* Top KPI row */}
      <div className="dash-grid dash-grid-4" style={{ marginBottom: 22 }}>
        <div className="tg-card" style={{ padding: 18, background: "linear-gradient(135deg, rgba(0,242,255,0.12), rgba(255,255,255,0.02))", borderColor: "#00F2FF30" }}
             data-testid="kpi-tgc-price">
          <div style={{ fontSize: 11, color: "#00F2FF", letterSpacing: 1, display: "flex", alignItems: "center", gap: 6 }}>
            <Coins size={13} /> TGC / USDT
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6, fontFeatureSettings: '"tnum"' }}>
            ${fmt(market?.tgc_price_usdt, 6)}
          </div>
          <div style={{ fontSize: 11, color: "#10F4A7" }}>+{fmt(market?.price_appreciation_pct, 4)}%</div>
        </div>
        <div className="tg-card" style={{ padding: 18, background: "linear-gradient(135deg, rgba(255,215,0,0.10), rgba(255,255,255,0.02))", borderColor: "#FFD70030" }}
             data-testid="kpi-tgc-balance">
          <div style={{ fontSize: 11, color: "#FFD700", letterSpacing: 1 }}>YOUR TGC BALANCE</div>
          <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6, fontFeatureSettings: '"tnum"' }}>
            {ledger ? fmt(ledger.tgc_token_balance, 2) : "—"}
          </div>
          <div style={{ fontSize: 11, color: "#8B94B8" }}>TGC · locked pre-launch</div>
        </div>
        <div className="tg-card" style={{ padding: 18, background: "linear-gradient(135deg, rgba(16,244,167,0.12), rgba(255,255,255,0.02))", borderColor: "#10F4A730" }}
             data-testid="kpi-hwm">
          <div style={{ fontSize: 11, color: "#10F4A7", letterSpacing: 1, display: "flex", alignItems: "center", gap: 6 }}>
            <Lock size={13} /> HIGH-WATERMARK
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6, fontFeatureSettings: '"tnum"' }}>
            ${fmt(ledger?.fiat_stability_high_watermark)}
          </div>
          <div style={{ fontSize: 11, color: "#8B94B8" }}>peak · downside-locked</div>
        </div>
        <div className="tg-card" style={{ padding: 18, background: "linear-gradient(135deg, rgba(188,19,254,0.12), rgba(255,255,255,0.02))", borderColor: "#BC13FE30" }}
             data-testid="kpi-extractable">
          <div style={{ fontSize: 11, color: "#BC13FE", letterSpacing: 1 }}>EXTRACTABLE</div>
          <div style={{ fontSize: 24, fontWeight: 900, marginTop: 6, fontFeatureSettings: '"tnum"' }}>
            ${fmt(ledger?.extractable_usdt)}
          </div>
          <div style={{ fontSize: 11, color: "#8B94B8" }}>max(HWM, current fair)</div>
        </div>
      </div>

      {/* Account open OR account details */}
      {!state.exists && (
        <div className="tg-card" style={{ padding: 22, marginBottom: 20 }} data-testid="corp-open-panel">
          <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Open your Hybrid Corporate Account</h3>
          <p style={{ fontSize: 12, color: "#8B94B8", marginTop: 0 }}>
            Multi-currency (USDT · USD · EUR · AED · SGD) · bound to your platform validator node · protected by the Fiat Stability Lock.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <input placeholder="Corporate legal name" value={form.legal_name}
              onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
              data-testid="corp-legal-name"
              style={inputStyle} />
            <input placeholder="Jurisdiction (AIFC / DIFC / DE / …)" value={form.jurisdiction}
              onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
              data-testid="corp-jurisdiction"
              style={inputStyle} />
            <select value={form.primary_currency}
              onChange={(e) => setForm({ ...form, primary_currency: e.target.value })}
              data-testid="corp-primary-ccy"
              style={inputStyle}>
              {["USDT", "USD", "EUR", "AED", "SGD"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button data-testid="corp-open-btn" onClick={openAccount} disabled={busy}
              style={btnPrimary}>Open Account</button>
          </div>
        </div>
      )}

      {state.exists && corp && (
        <div className="tg-card" style={{ padding: 22, marginBottom: 20 }} data-testid="corp-details-panel">
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15 }}>{corp.legal_name}</h3>
              <div style={{ fontSize: 11, color: "#8B94B8", marginTop: 4 }}>
                <code>{corp.corp_id}</code> · {corp.jurisdiction} · validator <code>{corp.validator_node_id}</code>
              </div>
            </div>
            <div style={{ padding: "6px 10px", borderRadius: 999, background: "rgba(16,244,167,0.10)", color: "#10F4A7", fontSize: 11, alignSelf: "flex-start" }}>
              {corp.status}
            </div>
          </div>
          <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            {Object.entries(corp.balances || {}).map(([ccy, bal]) => (
              <div key={ccy} data-testid={`corp-balance-${ccy}`}
                style={{ padding: 12, borderRadius: 10, background: "rgba(0,0,0,0.30)", border: "1px solid rgba(255,255,255,0.05)" }}>
                <div style={{ fontSize: 10, color: "#5F6884", letterSpacing: 1 }}>{ccy}</div>
                <div style={{ fontSize: 20, fontWeight: 800, fontFeatureSettings: '"tnum"' }}>{fmt(bal, 2)}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 20, padding: 16, borderRadius: 10, background: "rgba(255,215,0,0.05)", border: "1px solid rgba(255,215,0,0.25)" }}>
            <h4 style={{ margin: "0 0 8px", fontSize: 13, color: "#FFD700" }}>Fund the corporate account</h4>
            <p style={{ fontSize: 11, color: "#8B94B8", marginTop: 0 }}>
              Tops up the primary-currency ledger and (optionally) mints matching TGC tokens on the corporate ledger row.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "center" }}>
              <select value={fundForm.currency} onChange={(e) => setFundForm({ ...fundForm, currency: e.target.value })}
                data-testid="corp-fund-ccy" style={inputStyle}>
                {["USDT", "USD", "EUR", "AED", "SGD"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input type="number" value={fundForm.amount}
                onChange={(e) => setFundForm({ ...fundForm, amount: Number(e.target.value) })}
                data-testid="corp-fund-amount" style={inputStyle} />
              <label style={{ fontSize: 11, color: "#8B94B8", display: "flex", gap: 4, alignItems: "center" }}>
                <input type="checkbox" data-testid="corp-fund-tgc"
                  checked={fundForm.convert_to_tgc}
                  onChange={(e) => setFundForm({ ...fundForm, convert_to_tgc: e.target.checked })} />
                Mint TGC
              </label>
            </div>
            <button data-testid="corp-fund-btn" onClick={fund} disabled={busy || !fundForm.amount}
              style={{ ...btnPrimary, marginTop: 10 }}>
              <Sparkles size={14} /> Fund Account
            </button>
          </div>
        </div>
      )}

      {/* Ledger details */}
      {ledger && ledger.tgc_token_balance > 0 && (
        <div className="tg-card" style={{ padding: 22 }} data-testid="corp-ledger-panel">
          <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Corporate Token Ledger — deposit history</h3>
          <div style={{ fontSize: 11, color: "#8B94B8", marginBottom: 10 }}>
            Avg entry price <b>${fmt(ledger.avg_entry_price_usdt, 6)}</b> USDT · total deposited <b>${fmt(ledger.total_usdt_deposited)}</b> USDT
          </div>
          <table className="dash-table">
            <thead><tr><th>Deposit</th><th>Source</th><th>USDT in</th><th>TGC minted</th><th>Entry price</th><th>When</th></tr></thead>
            <tbody>
              {(ledger.deposits || []).slice(-15).reverse().map((d, i) => (
                <tr key={d.deposit_id || i}>
                  <td><code>{d.deposit_id}</code></td>
                  <td><b>{d.source_type}</b></td>
                  <td>${fmt(d.usdt_deposited)}</td>
                  <td style={{ color: "#FFD700", fontWeight: 700 }}>{fmt(d.tgc_minted, 4)}</td>
                  <td>${fmt(d.entry_price, 6)}</td>
                  <td style={{ fontSize: 11, color: "#5F6884" }}>{new Date(d.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  padding: 10, background: "rgba(0,0,0,0.25)", color: "#E7ECFF",
  border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontSize: 13,
};
const btnPrimary = {
  padding: "10px 16px", borderRadius: 10, border: "none", cursor: "pointer",
  background: "linear-gradient(135deg, #FFD700, #FF9F43)", color: "#050914",
  fontWeight: 800, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6,
};
